/**
 * Renders a Scryfall mana_cost string ("{2}{U}{U}") as a row of styled
 * pip glyphs (TNK-121). Used in the shop card detail title row + the
 * CardDetailModal — both surfaces just pass the raw `cost` string and
 * this component handles parse + render.
 *
 * Each pip is a small circle with the canonical MTG color (W=ivory,
 * U=blue, B=charcoal, R=red, G=green, C=light slate, generic=neutral
 * grey). Hybrid pips ({U/R}) split the background diagonally; Phyrexian
 * ({U/P}) gets a darker outer ring. The pip's inner glyph is the
 * Scryfall letter — no SVG sprite or external font, so the component
 * works offline and matches the rest of the shop's hand-built look.
 *
 * `size` controls the diameter in pixels (default 18, fits the card
 * title row). Returns `null` for empty / land-style costs so the caller
 * doesn't have to guard.
 */
import {
  manaPipKind,
  parseManaCost,
  type ManaPipKind,
} from "@tcg-web/normalize";

interface ManaCostProps {
  cost: string | null | undefined;
  /** Pip diameter in pixels; the inner glyph scales from this. Default 18. */
  size?: number;
  /** Extra Tailwind classes for the wrapping flex row (gap / alignment). */
  className?: string;
}

interface PipPalette {
  bg: string;
  text: string;
  ring?: string;
}

const PIP_COLORS: Record<ManaPipKind, PipPalette> = {
  white: { bg: "#f6efd2", text: "#1a1a1a" },
  blue: { bg: "#9fd0f0", text: "#0e1f2e" },
  black: { bg: "#3f3a3a", text: "#e8e3dc" },
  red: { bg: "#f0998d", text: "#2a0e0a" },
  green: { bg: "#a3cba0", text: "#102014" },
  colorless: { bg: "#cdd2d8", text: "#1a1f2a" },
  generic: { bg: "#d2c8b8", text: "#1a1a1a" },
  variable: { bg: "#d2c8b8", text: "#1a1a1a" },
  snow: { bg: "#dbeaf0", text: "#1a2a36" },
  hybrid: { bg: "#d2c8b8", text: "#1a1a1a" }, // overridden per-token below
  phyrexian: { bg: "#3f3a3a", text: "#e8e3dc", ring: "#5a5a5a" },
  unknown: { bg: "#5a5a5a", text: "#e8e3dc" },
};

/** For hybrid pips, return the two halves' kinds in print order, e.g.
 *  "U/R" → ["blue","red"], "2/W" → ["generic","white"]. */
function hybridHalves(token: string): [ManaPipKind, ManaPipKind] | null {
  const parts = token.split("/");
  if (parts.length !== 2) return null;
  return [manaPipKind(parts[0]), manaPipKind(parts[1])];
}

/** Visible glyph inside the pip — uppercase letter, digit, or `Φ` for the
 *  Phyrexian token (which prints as `U/P` but reads as the colored letter
 *  inside a phyrexian ring). */
function pipGlyph(token: string): string {
  const t = token.trim().toUpperCase();
  if (t.endsWith("/P")) return t.slice(0, -2);
  if (t.includes("/")) return t.split("/").join("/");
  return t;
}

function Pip({ token, size }: { token: string; size: number }) {
  const kind = manaPipKind(token);
  const palette = PIP_COLORS[kind];
  const glyph = pipGlyph(token);
  const halves = kind === "hybrid" ? hybridHalves(token) : null;

  // Hybrid: split background diagonally between the two halves.
  const background = halves
    ? `linear-gradient(135deg, ${PIP_COLORS[halves[0]].bg} 0% 50%, ${PIP_COLORS[halves[1]].bg} 50% 100%)`
    : palette.bg;
  const color = halves ? PIP_COLORS[halves[0]].text : palette.text;
  // Phyrexian's outer ring lives on box-shadow so the inner circle keeps
  // its own background — a border would eat space we don't have at 18px.
  const ring = palette.ring ? `0 0 0 2px ${palette.ring}` : undefined;

  const fontSize = Math.round(size * 0.62);
  return (
    <span
      aria-label={`mana cost ${token}`}
      className="inline-flex flex-shrink-0 items-center justify-center rounded-full font-bold leading-none"
      style={{
        width: size,
        height: size,
        background,
        color,
        fontSize,
        boxShadow: ring,
        fontFamily: "var(--font-mono, ui-monospace, monospace)",
      }}
    >
      {glyph}
    </span>
  );
}

export function ManaCost({ cost, size = 18, className = "" }: ManaCostProps) {
  const tokens = parseManaCost(cost);
  if (tokens.length === 0) return null;
  return (
    <span className={`inline-flex items-center gap-1 ${className}`}>
      {tokens.map((t, i) => (
        <Pip key={i} token={t} size={size} />
      ))}
    </span>
  );
}
